import { atom } from "jotai";
import { atomWithStorage, createJSONStorage, unstable_withStorageValidator as withStorageValidator } from "jotai/utils";
import type { Product, ChatMessage, TabName, InsertPosition } from "@/types";
import {
  changeChatSelection,
  emptyChatSelection,
  isChatSelection,
  publishOfficeSelection,
  type ChatSelection,
} from "@/utils/chat-selection";

export const activeTabAtom = atom<TabName>("chat");
// These are compatibility views for DDQ answer cards, never a second configuration source.
export const organizationIdAtom = atom("");
export const productAtom = atom<Product | null>(null);

export interface SelectedRequest {
  id: string;
  title: string;
}

export const requestAtom = atom<SelectedRequest | null>(null);

export const messageHistoryAtom = atom<ChatMessage[]>([]);
export const chatThreadIdAtom = atom<string | null>(null);
export const chatSessionAtom = atom(0);
export const resetChatAtom = atom(null, (get, set) => {
  set(chatSessionAtom, get(chatSessionAtom) + 1);
  set(chatThreadIdAtom, null);
  set(messageHistoryAtom, []);
});

type StorageChange =
  | { storageChange: "current"; value: ChatSelection }
  | { storageChange: "saved"; value: Record<string, ChatSelection> };
const storageListeners = new Set<(change: StorageChange) => void>();
const selectionStorage = withStorageValidator(isChatSelection)(createJSONStorage());
const subscribeToSelection = selectionStorage.subscribe;
if (subscribeToSelection)
  selectionStorage.subscribe = (key, callback, initialValue) =>
    subscribeToSelection(
      key,
      (value) => {
        for (const listener of storageListeners) listener({ storageChange: "current", value });
        callback(value);
      },
      initialValue
    );
const storedSelectionAtom = atomWithStorage("aiirChatSelection", emptyChatSelection, selectionStorage, {
  getOnInit: true,
});
const savedSelectionsStorage = withStorageValidator(
  (value: unknown): value is Record<string, ChatSelection> =>
    !!value &&
    typeof value === "object" &&
    Object.entries(value).every(([userId, selection]) => isChatSelection(selection) && selection.userId === userId)
)(createJSONStorage());
const subscribeToSavedSelections = savedSelectionsStorage.subscribe;
if (subscribeToSavedSelections)
  savedSelectionsStorage.subscribe = (key, callback, initialValue) =>
    subscribeToSavedSelections(
      key,
      (value) => {
        for (const listener of storageListeners) listener({ storageChange: "saved", value });
        callback(value);
      },
      initialValue
    );
const savedSelectionsAtom = atomWithStorage<Record<string, ChatSelection>>(
  "aiirOfficeSelections",
  {},
  savedSelectionsStorage,
  { getOnInit: true }
);
// Each taskpane owns its active account; changing accounts must not toggle another pane's user.
const activeOfficeUserAtom = atom<string | null>(null);

function selectionForUser(
  userId: string | null,
  lastSelection: ChatSelection,
  saved: Record<string, ChatSelection>
): ChatSelection {
  if (userId === null || lastSelection.userId === userId) return lastSelection;
  if (!userId) return emptyChatSelection;
  return saved[userId] ?? { ...emptyChatSelection, userId };
}

export const chatSelectionAtom = atom(
  (get): ChatSelection =>
    selectionForUser(get(activeOfficeUserAtom), get(storedSelectionAtom), get(savedSelectionsAtom)),
  (get, set, next: ChatSelection | StorageChange) => {
    const previous = get(chatSelectionAtom);
    if ("storageChange" in next) {
      const incoming = selectionForUser(
        get(activeOfficeUserAtom),
        next.storageChange === "current" ? next.value : get(storedSelectionAtom),
        next.storageChange === "saved" ? next.value : get(savedSelectionsAtom)
      );
      if (JSON.stringify(previous) !== JSON.stringify(incoming)) publishOfficeSelection(null);
      return;
    }
    if (JSON.stringify(previous) === JSON.stringify(next)) return;
    publishOfficeSelection(null);
    const saved = { ...get(savedSelectionsAtom) };
    if (previous.userId) saved[previous.userId] = previous;
    if (next.userId) saved[next.userId] = next;
    set(savedSelectionsAtom, saved);
    set(activeOfficeUserAtom, next.userId);
    set(storedSelectionAtom, next);
    set(resetChatAtom);
  }
);
chatSelectionAtom.onMount = (setSelection) => {
  // Check the effective account selection before Jotai applies either external storage update.
  const listener = (change: StorageChange) => setSelection(change);
  storageListeners.add(listener);
  return () => {
    storageListeners.delete(listener);
  };
};
export const activateOfficeUserAtom = atom(null, (get, set, userId: string) => {
  if (get(activeOfficeUserAtom) === userId) return;
  publishOfficeSelection(null);
  const previous = get(chatSelectionAtom);
  if (previous.userId) set(savedSelectionsAtom, { ...get(savedSelectionsAtom), [previous.userId]: previous });
  set(activeOfficeUserAtom, userId);
  set(resetChatAtom);
});
export const updateChatSelectionAtom = atom(null, (get, set, change: Partial<ChatSelection>) => {
  const previous = get(chatSelectionAtom);
  const next = changeChatSelection(previous, change);
  if (JSON.stringify(previous) === JSON.stringify(next)) return;
  set(chatSelectionAtom, next);
});

export const insertPositionAtom = atomWithStorage<InsertPosition>("aiirInsertPosition", "2-cells-below");
export const fontNameAtom = atomWithStorage<string>("aiirFontName", "Calibri");
export const fontSizeAtom = atomWithStorage<number>("aiirFontSize", 11);
export const fontColorAtom = atomWithStorage<string>("aiirFontColor", "#000000");
